import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@bcc/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@bcc/components/ui/popover";
import { cn } from "@bcc/lib/utils";
import {
  setMultiSelectClearTestId,
  setMultiSelectContentTestId,
  setMultiSelectOptionTestId,
  setMultiSelectPillTestId,
  setMultiSelectSearchTestId,
  setMultiSelectTestId,
  setMultiSelectTriggerTestId,
} from "./multi-select.utils";

export interface MultiSelectOption {
  label: string;
  value: string;
  isDisabled?: boolean;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /** Instance identifier used to namespace all internal test IDs. Defaults to "multi-select". */
  identifier?: string;
  /** Overrides the root wrapper data-testid entirely. */
  testId?: string;
  clearAllText?: string;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled = false,
  className,
  identifier = "multi-select",
  testId,
  clearAllText,
}: MultiSelectProps) {
  const { t } = useTranslation();
  const listboxId = React.useId();
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  // Reset search query when the popover closes
  React.useEffect(() => {
    if (!isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset search query when the multi-select popover closes
      setQuery("");
    }
  }, [isOpen]);

  const resolvedPlaceholder = placeholder ?? t("components.multiSelect.placeholder");
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("components.multiSelect.searchPlaceholder");
  const resolvedEmptyMessage = emptyMessage ?? t("components.multiSelect.emptyMessage");
  const resolvedClearLabel = clearAllText ?? t("components.multiSelect.clearAll");

  const optionsByValue = React.useMemo(() => {
    return new Map(options.map((option) => [option.value, option]));
  }, [options]);

  const filteredOptions = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) => {
      return option.label.toLowerCase().includes(normalizedQuery) || option.value.toLowerCase().includes(normalizedQuery);
    });
  }, [options, query]);

  const selectedOptions = React.useMemo(() => {
    return value.map((selectedValue) => {
      const selectedOption = optionsByValue.get(selectedValue);
      if (selectedOption) {
        return selectedOption;
      }

      return {
        label: selectedValue,
        value: selectedValue,
      };
    });
  }, [optionsByValue, value]);

  const handleToggleValue = React.useCallback(
    (option: MultiSelectOption) => {
      if (disabled || option.isDisabled) {
        return;
      }

      if (value.includes(option.value)) {
        onChange(value.filter((selectedValue) => selectedValue !== option.value));
        return;
      }

      onChange([...value, option.value]);
    },
    [disabled, onChange, value]
  );

  const handleClearAll = React.useCallback(() => {
    // Locked (disabled) selections cannot be cleared — keep them so a required
    // option (e.g. the roaster's home ship-to country) always stays selected.
    onChange(value.filter((selectedValue) => optionsByValue.get(selectedValue)?.isDisabled));
  }, [onChange, value, optionsByValue]);

  const handleRemovePill = React.useCallback(
    (selectedValue: string) => {
      if (disabled || optionsByValue.get(selectedValue)?.isDisabled) {
        return;
      }
      onChange(value.filter((item) => item !== selectedValue));
    },
    [disabled, onChange, value, optionsByValue]
  );

  const selectedSummary = value.length === 0 ? resolvedPlaceholder : t("components.multiSelect.selectedCount", { count: value.length });

  const triggerAriaLabel = value.length === 0 ? resolvedPlaceholder : `${resolvedPlaceholder}, ${selectedSummary}`;

  const onWheel = React.useCallback((event: React.WheelEvent<HTMLDivElement>): void => event.stopPropagation(), []);
  return (
    <div className={cn("flex w-full flex-col gap-2", className)} data-testid={testId ?? setMultiSelectTestId(identifier)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-label={triggerAriaLabel}
            className={cn(
              "border-input data-[placeholder=true]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex min-h-9 w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
            )}
            data-placeholder={value.length === 0}
            data-testid={setMultiSelectTriggerTestId(identifier)}
          >
            <span className="truncate text-left">{selectedSummary}</span>
            <ChevronDownIcon className="size-4 opacity-50" />
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-[320px] p-2" align="start" testId={setMultiSelectContentTestId(identifier)}>
          <div className="flex flex-col gap-2">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={resolvedSearchPlaceholder}
              testId={setMultiSelectSearchTestId(identifier)}
            />
            <div className="max-h-60 overflow-y-auto" onWheel={onWheel}>
              {filteredOptions.length === 0 ? (
                <p className="text-muted-foreground px-2 py-2 text-sm">{resolvedEmptyMessage}</p>
              ) : (
                <ul role="listbox" aria-multiselectable="true" id={listboxId} className="space-y-1">
                  {filteredOptions.map((option) => {
                    const isSelected = value.includes(option.value);
                    const isOptionDisabled = disabled || option.isDisabled;

                    return (
                      <li key={option.value} role="none">
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          aria-disabled={isOptionDisabled}
                          onClick={() => handleToggleValue(option)}
                          disabled={isOptionDisabled}
                          className={cn(
                            "hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                            isOptionDisabled && "text-muted-foreground cursor-not-allowed opacity-50"
                          )}
                          data-testid={setMultiSelectOptionTestId(identifier, option.value)}
                        >
                          <span
                            className={cn(
                              "border-input flex size-4 items-center justify-center rounded-[4px] border",
                              isSelected && "bg-primary text-primary-foreground border-primary"
                            )}
                          >
                            {isSelected ? <CheckIcon className="size-3" /> : null}
                          </span>
                          <span>{option.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {selectedOptions.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {selectedOptions.map((selectedOption) => {
            const isPillLocked = disabled || !!selectedOption.isDisabled;

            return (
              <button
                key={selectedOption.value}
                type="button"
                disabled={isPillLocked}
                aria-label={t("components.multiSelect.removeSelected", {
                  label: selectedOption.label,
                })}
                onClick={() => handleRemovePill(selectedOption.value)}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/80 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                data-testid={setMultiSelectPillTestId(identifier, selectedOption.value)}
              >
                <span>{selectedOption.label}</span>
                {!selectedOption.isDisabled && <XIcon className="size-3" />}
              </button>
            );
          })}

          <button
            type="button"
            disabled={disabled}
            aria-label={resolvedClearLabel}
            onClick={handleClearAll}
            className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid={setMultiSelectClearTestId(identifier)}
          >
            {resolvedClearLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
}
